import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    HelixLoaderComponent,
    PageWrapper,
    UpdatePageHeader,
} from '../../../../components';
import InvalidPermissionsDialog from '../../../../components/invalid-permissions-dialog/InvalidPermissionsDialog';
import { checkForInvalidProperties } from '../../../../components/paired-input-selection-widget/paired-input-selection-helper';
import { GLOSSARY_METADATA_PATHS } from '../../../../constants/glossary-metadata-paths';
import { usePermission } from '../../../../core';
import {
    useDeleteGlossaryMetadata,
    useGlossaryMetadataById,
    useUpdateGlossaryMetadata,
} from '../../../../hooks';
import {
    initializePropertiesList,
    possiblyNavigateToMetadataSearch,
} from '../../../../lib';
import { AttributeAssociationItem } from '../../../edge-definitions/edge-definition-helper';
import {
    checkForCaseSensitiveDuplicates,
    defaultGlossary,
    GlossaryMetadataItem,
    glossaryMetadataSchema,
    glossaryValidationMessages,
} from '../../glossary-metadata-helper';
import { GlossaryMetadataForm } from '../../GlossaryMetadataForm';

export const UpdateGlossaryMetadata = (): React.JSX.Element => {
    const { id: glossaryId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { hasPermissions } = usePermission();

    const { mutate: deleteGlossaryMutation } = useDeleteGlossaryMetadata();
    const { mutate: updateGlossaryMutation } = useUpdateGlossaryMetadata();
    const { isLoading, isFetched, isSuccess, data } = useGlossaryMetadataById({
        id: glossaryId as string,
    });

    const [isChangesUnsaved, setIsChangesUnsaved] = useState<boolean>(false);
    const [openCancelModal, setOpenCancelModal] = useState<boolean>(false);
    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);
    const [aliases, setAliases] = useState<string[]>([]);
    const [abbreviations, setAbbreviations] = useState<string[]>([]);
    const [glossaryAssociationList, setGlossaryAssociationList] = useState<
        AttributeAssociationItem[]
    >([]);
    const [currentDescription, setCurrentDescription] = useState<string>('');
    const [currentGroupName, setCurrentGroupName] = useState<string>('');
    const [currentType, setCurrentType] = useState<string>('');

    const {
        formState: { errors },
        control,
        setValue,
        watch,
        setError,
    } = useForm<GlossaryMetadataItem>({
        mode: 'all',
        defaultValues: defaultGlossary,
        resolver: yupResolver(glossaryMetadataSchema),
    });
    const [term, description, groupName, type] = watch([
        'term',
        'description',
        'groupName',
        'type',
    ]);
    const hasDeletePermission = hasPermissions('deleteMetadata');

    const initializeGlossaryProperties = (properties: any): void => {
        const glossaryPropertyList = initializePropertiesList(properties);
        setGlossaryAssociationList(glossaryPropertyList);
    };

    const initializeData = (glossaryItem: GlossaryMetadataItem): void => {
        setValue('term', glossaryItem?.term ?? '');
        setValue('description', glossaryItem?.description ?? '');
        setValue('groupName', glossaryItem?.groupName ?? '');
        setValue('type', glossaryItem?.type ?? '');
        setCurrentDescription(glossaryItem?.description ?? '');
        setCurrentGroupName(glossaryItem?.groupName ?? '');
        setCurrentType(glossaryItem?.type ?? '');
        setAliases(glossaryItem?.aliases ?? []);
        setAbbreviations(glossaryItem?.abbreviations ?? []);
        initializeGlossaryProperties(glossaryItem?.properties);
    };

    useEffect(() => {
        if (data && isFetched && isSuccess) {
            initializeData(data?.data);
        }
    }, [data, isFetched, isSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (currentDescription !== description && description) {
            setCurrentDescription(description);
            setIsChangesUnsaved(true);
        }
    }, [description, currentDescription]);

    useEffect(() => {
        if (currentGroupName !== groupName && groupName) {
            setCurrentGroupName(groupName);
            setIsChangesUnsaved(true);
        }
    }, [groupName, currentGroupName]);

    useEffect(() => {
        if (currentType !== type && type) {
            setCurrentType(type);
            setIsChangesUnsaved(true);
        }
    }, [type, currentType]);

    const handleGlossaryMetadataNavigation = (): void => {
        possiblyNavigateToMetadataSearch(
            navigate,
            GLOSSARY_METADATA_PATHS.LANDING_PAGE,
            searchParams
        );
    };

    const updateGlossaryMetadataForm = (): void => {
        setIsChangesUnsaved(false);
        const updatedGlossary: GlossaryMetadataItem = {
            term: term,
            description: description,
            groupName: groupName,
            type: type,
            aliases: aliases,
            abbreviations: abbreviations,
            properties: glossaryAssociationList.reduce(
                (acc: any, item: AttributeAssociationItem) => {
                    acc[item.attributeAssociation.leftSideValue] =
                        item.attributeAssociation.rightSideValue;
                    return acc;
                },
                {}
            ),
        };

        updateGlossaryMutation(
            { updatedGlossary, id: glossaryId as string },
            {
                onSuccess: () => {
                    setTimeout(() => {
                        handleGlossaryMetadataNavigation();
                    }, 500);
                },
            }
        );
    };

    const possiblyUpdateGlossaryMetadataForm = (): void => {
        const permission = hasPermissions('editMetadata');
        if (permission) {
            updateGlossaryMetadataForm();
        } else {
            setDisplayPermissionDialog(true);
        }
    };

    const isValidCustomValidation = (): boolean => {
        const isDescriptionError = !!errors.description;
        const isGroupNameError = !!errors.groupName;
        const isTypeError = !!errors.type;

        const areAliasesDuplicated = checkForCaseSensitiveDuplicates(aliases);
        const areAbbreviationsDuplicated =
            checkForCaseSensitiveDuplicates(abbreviations);

        const { isListValid, updatedList } = checkForInvalidProperties(
            glossaryAssociationList
        );

        if (areAliasesDuplicated) {
            setError('aliases', {
                message: glossaryValidationMessages.duplicatedOptions,
            });
        }
        if (areAbbreviationsDuplicated) {
            setError('abbreviations', {
                message: glossaryValidationMessages.duplicatedOptions,
            });
        }

        setGlossaryAssociationList(updatedList);

        return (
            !isDescriptionError &&
            !isGroupNameError &&
            !isTypeError &&
            !areAbbreviationsDuplicated &&
            !areAliasesDuplicated &&
            isListValid
        );
    };

    const onSubmit = (): void => {
        if (isValidCustomValidation()) {
            possiblyUpdateGlossaryMetadataForm();
        }
    };

    const handleDeleteConfirmation = (): void => {
        deleteGlossaryMutation(
            { id: glossaryId as string },
            {
                onSuccess: () => {
                    setTimeout(() => {
                        handleGlossaryMetadataNavigation();
                    }, 500);
                },
            }
        );
    };

    const handleCancelButton = (): void => {
        if (isChangesUnsaved) {
            setOpenCancelModal(true);
        } else {
            handleGlossaryMetadataNavigation();
        }
    };

    const handleDialogClose = (): void => {
        setDisplayPermissionDialog(false);
    };

    const handleCancelModalConfirmation = (): void => {
        setOpenCancelModal(false);
        handleGlossaryMetadataNavigation();
    };

    const handleCancelModal = (): void => {
        setOpenCancelModal(false);
    };

    const handleFormChanged = (): void => {
        setIsChangesUnsaved(true);
    };

    const handleAliasesChanged = (value: string[]): void => {
        setAliases(value);
        handleFormChanged();
    };

    const handleAbbreviationsChanged = (value: string[]): void => {
        setAbbreviations(value);
        handleFormChanged();
    };

    const handlePropertyListChanged = (
        newList: AttributeAssociationItem[]
    ): void => {
        setGlossaryAssociationList(newList);
        handleFormChanged();
    };

    if (isLoading) {
        return <HelixLoaderComponent />;
    }

    return (
        <PageWrapper title={'Edit Glossary'} includeBreadcrumbs={true}>
            <UpdatePageHeader
                isLoading={isLoading}
                handleSaveButton={onSubmit}
                headingText={'General Info'}
                deleteMenuItemText={'Delete Glossary'}
                handleCancelButton={handleCancelButton}
                checkForExternalDeletePermission
                hasDeletePermission={hasDeletePermission}
                isChangesUnsaved={isChangesUnsaved}
                openCancelModal={openCancelModal}
                handleCancelModalConfirmation={handleCancelModalConfirmation}
                handleCancelModal={handleCancelModal}
                unsavedChangesConfirmationText={
                    'This glossary has unsaved changes. Are you sure you want to close without saving? You will lose all changes.'
                }
                confirmationModalConfig={{
                    confirmationButtonText: 'Yes, Delete',
                    confirmationHeadingText: 'Confirm Glossary Deletion',
                    handleConfirmation: handleDeleteConfirmation,
                    confirmationCancelButtonText: 'No, Cancel',
                    hasConfirmationTextField: false,
                    confirmationSubHeadingText: (
                        <>
                            Are you sure you would like to delete your glossary?
                        </>
                    ),
                }}
                menuWidth={'202px'}
            />
            <GlossaryMetadataForm
                isEdit
                term={term}
                control={control}
                errors={errors}
                aliases={aliases}
                abbreviations={abbreviations}
                onAliasesChanged={handleAliasesChanged}
                onAbbreviationsChanged={handleAbbreviationsChanged}
                onPropertiesListChanged={handlePropertyListChanged}
                glossaryAssociationList={glossaryAssociationList}
            />
            {displayPermissionDialog && (
                <InvalidPermissionsDialog
                    open={displayPermissionDialog}
                    onClose={handleDialogClose}
                />
            )}
        </PageWrapper>
    );
};
